/**
 * Tela mais larga que 16:10 (celular em paisagem): o canvas ganha a largura da janela, em vez de
 * sobrar faixa lateral no Scale.FIT. A altura continua 800 e as cenas continuam desenhadas em
 * 0..1280: a câmera principal é deslocada para essa área ficar centrada, e os fundos se estendem
 * pelas laterais (ver `GAME.MARGEM`). No tablet 16:10 a largura é 1280 e nada muda.
 */
import { GAME } from '../config/constants';

const JANELA_DE_COALESCENCIA = 16;

/**
 * @param {number} larguraJanela
 * @param {number} alturaJanela
 * @returns {{ largura: number, folga: number }} largura lógica do canvas (par, entre 1280 e o
 *   teto) e a folga de cada lado da área de 1280
 */
export function calcularTela (larguraJanela, alturaJanela) {
    if (!(larguraJanela > 0 && alturaJanela > 0)) return { largura: GAME.WIDTH, folga: 0 };
    const par = Math.round((GAME.HEIGHT * larguraJanela / alturaJanela) / 2) * 2;
    const largura = Math.min(GAME.LARGURA_TOTAL, Math.max(GAME.WIDTH, par));
    return { largura, folga: (largura - GAME.WIDTH) / 2 };
}

const medirJanela = () => [window.innerWidth, window.innerHeight];

/** Teclado virtual aberto: a janela encolhe, mas a largura do jogo não deve acompanhar. */
const campoEmEdicao = () => {
    const el = document.activeElement;
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable === true);
};

/**
 * @param {Phaser.Game} jogo
 * @param {object} [opcoes]
 * @param {() => [number, number]} [opcoes.medir] largura e altura da janela
 * @returns {() => void} desliga tudo
 */
export function ligarTelaLarga (jogo, { medir = medirJanela } = {}) {
    let folga = (jogo.scale.gameSize.width - GAME.WIDTH) / 2;
    const ouvintes = [];
    let pendente = null;

    const centralizar = (cena) => cena.cameras?.main?.setScroll(-folga || 0, 0);

    const ligarCenas = () => {
        jogo.scene.scenes.forEach((cena) => {
            const aoCriar = () => centralizar(cena);
            cena.sys.events.on('create', aoCriar);
            ouvintes.push(() => cena.sys.events.off('create', aoCriar));
        });
    };
    // O Phaser só registra as cenas ao ficar pronto, que pode ser depois deste ponto.
    if (jogo.scene.scenes.length > 0) ligarCenas();
    else jogo.events?.once('ready', ligarCenas);

    const acompanharJanela = () => {
        pendente = null;
        if (campoEmEdicao()) return;
        const { largura, folga: nova } = calcularTela(...medir());
        if (largura === jogo.scale.gameSize.width) return;
        jogo.scale.setGameSize(largura, GAME.HEIGHT);
        folga = nova;
        jogo.scene.getScenes(true).forEach(centralizar);
    };
    const agendar = () => {
        if (pendente === null) pendente = setTimeout(acompanharJanela, JANELA_DE_COALESCENCIA);
    };
    ['resize', 'orientationchange'].forEach((evento) => {
        window.addEventListener(evento, agendar);
        ouvintes.push(() => window.removeEventListener(evento, agendar));
    });

    return () => {
        ouvintes.forEach((desfazer) => desfazer());
        jogo.events?.off('ready', ligarCenas);
        if (pendente !== null) clearTimeout(pendente);
        pendente = null;
    };
}
